import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, ViewChild, ElementRef, OnDestroy, inject } from '@angular/core';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectCreationService, ProjectCreationResult } from '../../../../shared/project-creation.service';
import { getProjectIdValidationError, PROJECT_ID_RULES, suggestProjectId } from '../../../../shared/project-id.util';

export interface NewProjectData {
  projectId: string;
  projectName: string;
  file: File | null;
  pages: 'all' | 'custom' | 'none';
  customPages?: string;
  taskIds?: string[];
}

export interface ProjectFieldInfoData {
  title: string;
  paragraphs: readonly string[];
  example: string;
}

/** Detailed, keyboard-accessible guidance opened from a project identity field. */
@Component({
  selector: 'app-project-field-info-dialog',
  imports: [MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      @for (paragraph of data.paragraphs; track paragraph) {
        <p>{{ paragraph }}</p>
      }
      <p class="field-info-example">
        <strong>Fictional multifamily example</strong>
        <span>{{ data.example }}</span>
      </p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .field-info-example {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 12px 16px;
        border-radius: 12px;
        background: var(--mat-sys-surface-container-low);
      }
    `,
  ],
})
export class ProjectFieldInfoDialogComponent {
  readonly data = inject<ProjectFieldInfoData>(MAT_DIALOG_DATA);
}

/** Multi-step dialog guiding users through creating a new project: naming, uploading plan PDFs, and tracking ingestion progress. */
@Component({
  selector: 'app-new-project-dialog',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatProgressBarModule,
    MatSnackBarModule,
  ],
  templateUrl: './new-project-dialog.component.html',
  styleUrls: ['./new-project-dialog.component.scss'],
})
export class NewProjectDialogComponent implements OnDestroy {
  dialogRef = inject<MatDialogRef<NewProjectDialogComponent>>(MatDialogRef);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private projectCreationService = inject(ProjectCreationService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  // Form fields
  readonly projectNameControl = new FormControl('', {
    nonNullable: true,
    validators: [control => (control.value.trim() ? null : { required: true })],
  });
  readonly projectIdControl = new FormControl('', {
    nonNullable: true,
    validators: [control => (getProjectIdValidationError(control.value.trim()) ? { projectId: true } : null)],
  });
  readonly immediateErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: control => !!control?.invalid && (control.dirty || control.touched),
  };
  readonly projectIdRules = PROJECT_ID_RULES;
  private projectIdManuallyEdited = false;
  uploadChoice: 'now' | 'later' = 'now';
  selectedFile: File | null = null;
  pages: 'all' | 'custom' | 'none' = 'all';
  customPages: string = '';
  loading: boolean = false;

  // Project creation state
  projectCreated: boolean = false;
  creationResult: ProjectCreationResult | null = null;
  private isCreating: boolean = false;

  constructor() {
    this.projectNameControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(projectName => {
      if (!this.projectIdManuallyEdited) {
        const suggestion = suggestProjectId(projectName);
        this.projectIdControl.setValue(suggestion);
        if (projectName.trim() && !suggestion) {
          this.projectIdControl.markAsTouched();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get projectName(): string {
    return this.projectNameControl.value;
  }

  set projectName(value: string) {
    this.projectNameControl.setValue(value);
  }

  get projectId(): string {
    return this.projectIdControl.value;
  }

  set projectId(value: string) {
    this.projectIdControl.setValue(value);
  }

  /** Stops automatic suggestions after the user explicitly edits the ID. */
  onProjectIdInput(): void {
    this.projectIdManuallyEdited = true;
  }

  /** Opens detailed guidance for the human-readable project name. */
  openProjectNameInfo(): void {
    this.openFieldInfo({
      title: 'About Project Name',
      paragraphs: [
        'Enter the recognizable name residents, owners, or project teams use for this multifamily property or development.',
        'This display name appears in project lists and headings. It can include spaces and punctuation and can change without changing the Project ID.',
      ],
      example: 'Juniper Court Apartments',
    });
  }

  /** Opens detailed guidance for the stable technical project identifier. */
  openProjectIdInfo(): void {
    this.openFieldInfo({
      title: 'About Project ID',
      paragraphs: [
        'Choose a stable lowercase identifier used in URLs and storage. An address-based value helps keep it unique and should remain unchanged when the Project Name changes.',
        this.projectIdRules,
      ],
      example: 'us-ca-redwood-city-1250-juniper-court',
    });
  }

  private openFieldInfo(data: ProjectFieldInfoData): void {
    this.dialog.open(ProjectFieldInfoDialogComponent, {
      data,
      ariaLabel: data.title,
      autoFocus: 'dialog',
      restoreFocus: true,
      width: 'min(440px, calc(100vw - 32px))',
      maxWidth: '440px',
    });
  }

  get projectIdError(): string | null {
    if (this.projectIdControl.hasError('alreadyExists')) {
      return 'A project with this ID already exists. Choose a different Project ID.';
    }
    return getProjectIdValidationError(this.projectId.trim());
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];

      if (!this.selectedFile.type.includes('pdf')) {
        this.snackBar.open('Please select a PDF file', 'Close', { duration: 3000 });
        this.selectedFile = null;
        input.value = '';
        return;
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onConfirm(): Promise<void> {
    this.projectNameControl.markAsTouched();
    this.projectIdControl.markAsTouched();
    if (this.projectNameControl.invalid || this.projectIdControl.invalid) {
      return;
    }

    if (this.uploadChoice === 'now' && !this.selectedFile) {
      this.snackBar.open('Please select a PDF file', 'Close', { duration: 3000 });
      return;
    }

    if (this.uploadChoice === 'now' && this.pages === 'custom' && !this.customPages.trim()) {
      this.snackBar.open('Please enter page numbers for custom selection', 'Close', { duration: 3000 });
      return;
    }

    if (this.uploadChoice === 'now' && this.pages === 'custom') {
      const pagePattern = /^(\d+(-\d+)?)(,\s*\d+(-\d+)?)*$/;
      if (!pagePattern.test(this.customPages.trim())) {
        this.snackBar.open('Please enter valid page numbers (e.g., 1,2,3-5,7)', 'Close', { duration: 3000 });
        return;
      }
    }

    await this.createProject();
  }

  /** Derives the effective `pages` value sent to the service. */
  private get effectivePages(): 'all' | 'custom' | 'none' {
    return this.uploadChoice === 'later' ? 'none' : this.pages;
  }

  private async createProject(): Promise<void> {
    if (this.isCreating) {
      ClientLoggingService.log(
        'WARN',
        `[NewProjectDialog] Project creation already in progress for: ${this.projectId}`
      );
      return;
    }

    try {
      this.isCreating = true;
      this.loading = true;
      ClientLoggingService.log('INFO', `[NewProjectDialog] Starting project creation: ${this.projectId}`);

      const result = await this.projectCreationService.createProject(
        this.projectId.trim(),
        this.projectName.trim(),
        this.uploadChoice === 'now' ? this.selectedFile : null,
        this.effectivePages,
        this.effectivePages === 'custom' ? this.customPages.trim() : undefined
      );

      this.loading = false;
      this.isCreating = false;
      this.creationResult = result;

      if (result.success) {
        this.projectCreated = true;
        this.creationResult = result;

        const taskCount = result.taskIds?.length || 0;
        const message =
          taskCount > 0
            ? `Project "${this.projectId}" created successfully! ${taskCount} page(s) are being processed in the background. You can monitor progress on the Tasks page.`
            : result.message || `Project "${this.projectId}" created successfully!`;

        const actionText = taskCount > 0 ? 'View Tasks' : 'Close';

        this.snackBar
          .open(message, actionText, {
            duration: 8000,
            panelClass: ['success-snackbar'],
          })
          .onAction()
          .subscribe(() => {
            if (taskCount > 0) {
              this.router.navigate(['/projects', result.projectId, 'tasks']);
            }
          });

        this.dialogRef.close({
          projectId: result.projectId,
          projectName: this.projectName.trim(),
          file: this.uploadChoice === 'now' ? this.selectedFile : null,
          pages: this.effectivePages,
          customPages: this.effectivePages === 'custom' ? this.customPages.trim() : undefined,
          taskIds: result.taskIds,
        } as NewProjectData);
      } else {
        if (result.errorField === 'projectId') {
          this.projectIdControl.setErrors({ ...(this.projectIdControl.errors ?? {}), alreadyExists: true });
          this.projectIdControl.markAsTouched();
        } else {
          this.snackBar.open(result.error || 'Failed to create project', 'Close', { duration: 5000 });
        }
      }
    } catch (error) {
      this.loading = false;
      this.isCreating = false;
      ClientLoggingService.log('ERROR', '[NewProjectDialog] Error creating project:', error);
      this.snackBar.open('Error creating project', 'Close', { duration: 5000 });
    }
  }

  get isValid(): boolean {
    if (this.projectNameControl.invalid || this.projectIdControl.invalid) return false;
    if (this.uploadChoice === 'later') return true;
    if (!this.selectedFile) return false;
    if (this.pages === 'custom' && !/^(\d+(-\d+)?)(,\s*\d+(-\d+)?)*$/.test(this.customPages.trim())) return false;
    return true;
  }

  clearFile(): void {
    this.selectedFile = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
